import {getAddictionUnit, getRetentionBucketName} from 'common/report/util/retention/util';
import {abbreviateNumber, formatPercent, htmlEncodeString} from 'common/util';
import {TooltipItemType} from 'common/components/chart-tooltip/types';
import {RetentionType} from 'common/report/util/retention/types';
import {getFrequencyTooltipSubtitle, isAddiction} from 'common/report/util/retention/util';
import {AGGREGATED_ROW_NAME} from 'common/report/util/retention/constants';
import {getDataGroupName, isSegmented} from 'common/report/data/retention/format';
import {RetentionBookmarkParams} from 'common/report/util/retention/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';

import type {LineChartRow, RetentionCurveChart} from './index';

// Axis label functions

export function retentionBucketLabel(bookmarkParams: RetentionBookmarkParams) {
  return function (value: number) {
    return getRetentionBucketName({bucketIndex: value, bookmarkParams});
  };
}

export function frequencyBucketLabel(unit: string) {
  return function (value: number) {
    return `${value + 2} ${getAddictionUnit(unit)}s`;
  };
}

export function bucketLabeler(bookmarkParams: RetentionBookmarkParams) {
  return isAddiction(bookmarkParams)
    ? frequencyBucketLabel(bookmarkParams.date_unit)
    : retentionBucketLabel(bookmarkParams);
}

export function percentLabel(value: number) {
  return formatPercent(value, 0);
}

export function absoluteLabel(value: number) {
  return value >= 10000 ? abbreviateNumber(value) : `${value}`;
}

export function percentOrAbsoluteLabel(isPercentage: boolean) {
  return isPercentage ? percentLabel : absoluteLabel;
}

// Axis size functions
const MAX_X_TICKS = 20;
export function computeTicks(maxTicks = MAX_X_TICKS) {
  return function (min: number, max: number): number[] {
    const xTicks = [min];
    const skip = max - min + 1 > maxTicks ? Math.floor((max - min + 1) / maxTicks) : 0;
    while (min <= max) {
      if (min > xTicks[xTicks.length - 1] + skip) {
        xTicks.push(min);
      }
      min += 1;
    }
    return xTicks;
  };
}

// Chart description functions

// Tooltip generating functions
export function getLineChartTooltip(lineChart: RetentionCurveChart, bookmarkParams: RetentionBookmarkParams) {
  return function (x: number, _y: number, _series: any, idx: number) {
    const row = lineChart.selectedSegmentsRows[idx];
    const cell = row.data[x];

    const dataGroupName = getDataGroupName(bookmarkParams.data_group_id);
    const isAggregated = isAggregateRow(row, bookmarkParams);
    const {titleSection, subtitle} = getTitleAndSubtitle(bookmarkParams, row, x);
    const sections = [
      {
        rows: [
          {
            items: titleSection,
          },
          {
            items: [{text: subtitle, type: TooltipItemType.Detail}],
          },
        ],
      },
      {
        rows: [
          {
            items: [
              {text: cell.percent_value, type: TooltipItemType.Data},
              {
                text: isAddiction(bookmarkParams) ? `engaged` : `retention`,
                type: TooltipItemType.Text,
              },
            ],
          },
          {
            items: [
              {text: cell.absolute_value, type: TooltipItemType.Data},
              {
                text: isAggregated ? `${dataGroupName}s (average)` : `${dataGroupName}s`,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      },
    ];

    const footerText = cell.incomplete ? `footer-text="Incomplete ${bookmarkParams.date_unit}" ` : ``;

    return `<mp-chart-tooltip title-color=${row.color} ${footerText} sections="${htmlEncodeString(
      JSON.stringify(sections),
    )}">`;
  };
}

function getTitleAndSubtitle(bookmarkParams: RetentionBookmarkParams, row: LineChartRow, x: number) {
  const cell = row.data[x];
  const isAggregatedRow = isAggregateRow(row, bookmarkParams);

  const title = isAddiction(bookmarkParams)
    ? getTitle(isSegmented(bookmarkParams.group_by), isAggregatedRow, bookmarkParams.retention_type)
    : getRetentionBucketName({bucketIndex: x, bookmarkParams});

  const subtitle = isAddiction(bookmarkParams)
    ? getFrequencyTooltipSubtitle({
        cumulative: bookmarkParams.cumulative,
        dateUnit: bookmarkParams.date_unit as TimeUnit,
        bucketIndex: x,
      })
    : retentionTooltipSubtitle(
        isSegmented(bookmarkParams.group_by),
        isAggregatedRow,
        bookmarkParams.retention_type,
        cell.incomplete,
      );

  const titleSection = [{text: title, type: TooltipItemType.Strong}];

  return {subtitle, titleSection};

  function getTitle(segmented: boolean, isAggregated: boolean, retentionType: RetentionType) {
    if (segmented) {
      return row.raw_head;
    } else if (isAggregated) {
      return AGGREGATED_ROW_NAME(retentionType);
    } else {
      return `Users entered on ${cell.entered_value}`;
    }
  }

  function retentionTooltipSubtitle(
    segmented: boolean,
    isAggregated: boolean,
    retentionType: RetentionType,
    incomplete: boolean,
  ) {
    return (
      getTitle(segmented, isAggregated, retentionType) +
      (isAggregatedRow && !incomplete ? ` in completed intervals` : ``)
    );
  }
}

function isAggregateRow(row: LineChartRow, bookmarkParams: RetentionBookmarkParams) {
  return (
    isSegmented(bookmarkParams.group_by) || row.display_head === AGGREGATED_ROW_NAME(bookmarkParams.retention_type)
  );
}

export function getStackedVizLineChartConfig(lineChart: RetentionCurveChart, bookmarkParams: RetentionBookmarkParams) {
  return {
    ...(lineChart.helpers.isFormattingTicksX() ? {formatTicksX: computeTicks()} : {}),
    formatXAxisLabel: bucketLabeler(bookmarkParams),
    formatYAxisLabel: percentOrAbsoluteLabel(bookmarkParams.percentage_view),
    formatTooltip: getLineChartTooltip(lineChart, bookmarkParams),
    chartData: lineChart.helpers.getChartData(),
    onHighChartClick: lineChart.helpers.handleHighChartClick,
    ...(lineChart.popover ? {popover: lineChart.popover} : {}),
  };
}
